import { Phone, PhoneCall, PhoneOff, Zap } from "lucide-react";

/** Incoming call (active_call mode) — user is the customer, AI seller is calling. */
export const IncomingCallOverlay = ({ scenario, realtimeAvailable, onAccept, onReject }) => (
  <div className="max-w-md mx-auto px-4 py-12 sm:py-16 text-center" data-testid="incoming-call-screen">
    <div className="nv-card p-8 sm:p-10 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 0%, rgba(31,203,79,0.4) 0%, transparent 70%)" }}
      />
      <div className="relative">
        <p className="label-eyebrow text-novatus-green animate-pulse">● Eingehender Anruf</p>
        <div className="mt-6 w-24 h-24 mx-auto rounded-full bg-novatus-green/15 border-2 border-novatus-green flex items-center justify-center pulse-green">
          <Phone className="w-10 h-10 text-novatus-green" />
        </div>
        <h2 className="font-display font-black text-2xl text-white mt-6">Novatus Energie</h2>
        <p className="text-sm text-novatus-text-2 mt-2">Demo-Anruf · {scenario?.title}</p>
        <p className="text-xs text-novatus-text-3 mt-1">Du nimmst die Rolle des Kunden ein</p>
        <p className="text-[10px] text-novatus-text-3 mt-2 leading-relaxed border-t border-novatus pt-2">
          🔒 Hinweis: Deine Sprach-Aufnahme wird zu <strong>Trainings- und Coaching-Zwecken</strong> verschlüsselt gespeichert (EU-Server, Hetzner Falkenstein) und nach <strong>90 Tagen automatisch gelöscht</strong>.
        </p>
        {realtimeAvailable && (
          <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-novatus-green/15 text-novatus-green border border-novatus-green/40">
            <Zap className="w-3 h-3" /> Live-Modus aktiv · niedrige Latenz
          </div>
        )}
        <div className="mt-8 flex items-center justify-center gap-4">
          <button
            onClick={onReject}
            className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-transform hover:scale-105"
            data-testid="reject-call-btn"
            title="Ablehnen"
          >
            <PhoneOff className="w-6 h-6" />
          </button>
          <button
            onClick={onAccept}
            className="px-6 h-14 rounded-full bg-novatus-green hover:bg-[#25E058] text-black font-bold flex items-center justify-center gap-2 transition-transform hover:scale-105 pulse-green"
            data-testid="accept-call-btn"
          >
            <PhoneCall className="w-5 h-5" /> Annehmen
          </button>
        </div>
      </div>
    </div>
    <p className="text-xs text-novatus-text-3 mt-6">
      Tipp: Beobachte, wie die KI strukturiert verkauft — übernimm Formulierungen für deine eigenen Calls.
    </p>
  </div>
);

/** Outgoing call (passive_call mode) — user is the seller, AI customer waits. */
export const OutgoingCallOverlay = ({ scenario, realtimeAvailable, onStart, onCancel }) => (
  <div className="max-w-md mx-auto px-4 py-12 sm:py-16 text-center" data-testid="outgoing-call-screen">
    <div className="nv-card p-8 sm:p-10 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 0%, rgba(31,203,79,0.4) 0%, transparent 70%)" }}
      />
      <div className="relative">
        <p className="label-eyebrow text-novatus-green">● Ausgehender Anruf</p>
        <div className="mt-6 w-24 h-24 mx-auto rounded-full bg-novatus-green/15 border-2 border-novatus-green flex items-center justify-center">
          <PhoneCall className="w-10 h-10 text-novatus-green" />
        </div>
        <h2 className="font-display font-black text-2xl text-white mt-6">{scenario?.title || "Kunden-Anruf"}</h2>
        <p className="text-sm text-novatus-text-2 mt-2">Du rufst den Kunden an · Du bist der Verkäufer</p>
        <p className="text-xs text-novatus-text-3 mt-3 leading-relaxed">
          Wenn du auf <span className="text-novatus-green font-bold">„Anruf starten"</span> tippst, hebt der Kunde nur kurz mit „Hallo?" ab.
          Dann <span className="text-novatus-green font-bold">eröffnest du</span> als Verkäufer das Gespräch — sprich einfach drauflos, das Mikrofon ist durchgehend offen.
        </p>
        <p className="text-[10px] text-novatus-text-3 mt-2 leading-relaxed border-t border-novatus pt-2">
          🔒 Hinweis: Deine Sprach-Aufnahme wird zu <strong>Trainings- und Coaching-Zwecken</strong> verschlüsselt gespeichert (EU-Server, Hetzner Falkenstein) und nach <strong>90 Tagen automatisch gelöscht</strong>.
        </p>
        {realtimeAvailable && (
          <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full bg-novatus-green/15 text-novatus-green border border-novatus-green/40" data-testid="realtime-badge">
            <Zap className="w-3 h-3" /> Live-Modus aktiv · niedrige Latenz
          </div>
        )}
        <button
          onClick={onStart}
          className="mt-8 w-full h-14 rounded-full bg-novatus-green hover:bg-[#25E058] text-black font-bold flex items-center justify-center gap-2 transition-transform hover:scale-105 pulse-green shadow-[0_0_30px_rgba(35,224,88,0.4)]"
          data-testid="start-call-btn"
        >
          <Phone className="w-5 h-5" /> Anruf starten
        </button>
        <button
          onClick={onCancel}
          className="mt-2 text-xs text-novatus-text-3 hover:text-white"
          data-testid="cancel-call-btn"
        >
          Abbrechen
        </button>
      </div>
    </div>
    <p className="text-xs text-novatus-text-3 mt-6">
      Tipp: Beginne mit einer freundlichen Eröffnung — der Kunde reagiert wie ein echter Lead.
    </p>
  </div>
);
